import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../api';
import type { AdminUserDto } from '../../api';
import { VerificationStatus } from '../../api/generated/boticaAPI.schemas';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DataGrid, type DataGridItem } from '../../components/ui/DataGrid';
import { Modal } from '../../components/ui/Modal';
import { Tooltip } from '../../components/ui/Tooltip';
import { useToast } from '../../components/ui/Toast';

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

const CRED_STATUS_LABEL: Record<VerificationStatus, string> = {
  [VerificationStatus.PendingDocuments]: 'Unverified',
  [VerificationStatus.PendingReview]: 'Pending',
  [VerificationStatus.Verified]: 'Verified',
  [VerificationStatus.Rejected]: 'Rejected',
};

const CRED_STATUS_BADGE: Record<VerificationStatus, 'neutral' | 'pending' | 'approved' | 'rejected'> = {
  [VerificationStatus.PendingDocuments]: 'neutral',
  [VerificationStatus.PendingReview]: 'pending',
  [VerificationStatus.Verified]: 'approved',
  [VerificationStatus.Rejected]: 'rejected',
};

/* ─── Role badge ─────────────────────────────────────────────────────────── */

function RoleBadge({ role }: { role: string }) {
  if (role === 'Founder') {
    return (
      <Badge
        variant="neutral"
        style={{ color: 'var(--terra-700)', background: 'var(--terra-100)' }}
      >
        {role}
      </Badge>
    );
  }
  return <Badge variant="neutral">{role}</Badge>;
}

/* ─── Section header ─────────────────────────────────────────────────────── */

function SectionHeader({ label }: { label: string }) {
  return (
    <p
      style={{
        margin: '0 0 8px',
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        color: 'var(--text-tertiary)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </p>
  );
}

/* ─── Skeleton ───────────────────────────────────────────────────────────── */

function DetailSkeleton() {
  const bar = (w: string, h = '14px', mb = '8px') => (
    <div
      style={{
        width: w,
        height: h,
        marginBottom: mb,
        borderRadius: 4,
        background: 'var(--warm-gray-100)',
        animation: 'skeleton-pulse 1.4s ease-in-out infinite',
      }}
    />
  );
  return (
    <div style={{ maxWidth: 760 }}>
      <style>{`@keyframes skeleton-pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      {bar('80px', '14px', '20px')}
      {bar('220px', '24px', '6px')}
      {bar('160px', '14px', '32px')}
      {bar('100%', '1px', '20px')}
      {bar('60%')}
      {bar('80%')}
      {bar('55%')}
      {bar('70%')}
    </div>
  );
}

/* ─── Confirmation modal ─────────────────────────────────────────────────── */

type RoleAction =
  | 'promote'
  | 'demote'
  | 'set-founder'
  | 'remove-founder'
  | 'set-verifier'
  | 'remove-verifier';

const ACTION_TITLE: Record<RoleAction, string> = {
  'promote': 'Promote to Admin?',
  'demote': 'Remove Admin role?',
  'set-founder': 'Grant Founder role?',
  'remove-founder': 'Remove Founder role?',
  'set-verifier': 'Grant Verifier role?',
  'remove-verifier': 'Remove Verifier role?',
};

const ACTION_BODY: Record<RoleAction, (name: string) => string> = {
  'promote': (n) => `${n} will be able to review pharmacist credentials and access the admin panel.`,
  'demote': (n) => `${n} will lose access to the admin panel and will no longer be able to review credentials.`,
  'set-founder': (n) => `${n} will receive the Founder role, with full access to manage other administrators' roles.`,
  'remove-founder': (n) => `${n} will lose the Founder role and will no longer be able to manage other administrators' roles.`,
  'set-verifier': (n) => `${n} will receive the Verifier role, with extended credential review capabilities.`,
  'remove-verifier': (n) => `${n} will lose the Verifier role.`,
};

const ACTION_CONFIRM_LABEL: Record<RoleAction, string> = {
  'promote': 'Promote',
  'demote': 'Remove Admin',
  'set-founder': 'Grant Founder',
  'remove-founder': 'Remove Founder',
  'set-verifier': 'Grant Verifier',
  'remove-verifier': 'Remove Verifier',
};

const DESTRUCTIVE_ACTIONS = new Set<RoleAction>(['demote', 'remove-founder', 'remove-verifier']);

type ConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  action: RoleAction;
  userName: string;
};

function ConfirmModal({ open, onClose, onConfirm, submitting, action, userName }: ConfirmModalProps) {
  return (
    <Modal open={open} onClose={onClose} title={ACTION_TITLE[action]} width={420}>
      <p
        style={{
          margin: '0 0 20px',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-secondary)',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        <strong style={{ color: 'var(--text-primary)' }}>{userName}</strong>{' '}
        {ACTION_BODY[action](userName).replace(`${userName} `, '')}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" size="md" disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={DESTRUCTIVE_ACTIONS.has(action) ? 'danger' : 'primary'}
          size="md"
          loading={submitting}
          onClick={onConfirm}
        >
          {ACTION_CONFIRM_LABEL[action]}
        </Button>
      </div>
    </Modal>
  );
}

/* ─── Role row ───────────────────────────────────────────────────────────── */

type RoleRowProps = {
  label: string;
  description: string;
  hasRole: boolean;
  isSelf: boolean;
  isLast?: boolean;
  onGrant: () => void;
  onRevoke: () => void;
};

function RoleRow({ label, description, hasRole, isSelf, isLast, onGrant, onRevoke }: RoleRowProps) {
  const button = (
    <Button
      variant={hasRole ? 'danger' : 'secondary'}
      size="sm"
      disabled={isSelf}
      onClick={hasRole ? onRevoke : onGrant}
    >
      {hasRole ? `Quitar ${label}` : `Dar ${label}`}
    </Button>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
        borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)',
        gap: 12,
      }}
    >
      <div>
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-primary)' }}>
          {label}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
          {description}
        </p>
      </div>
      {isSelf ? (
        <Tooltip content="You cannot modify your own roles">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </div>
  );
}

/* ─── Role mutation section ──────────────────────────────────────────────── */

type RoleMutationSectionProps = {
  user: AdminUserDto;
  viewerId: string | null;
  onRefetch: () => void;
};

function RoleMutationSection({ user, viewerId, onRefetch }: RoleMutationSectionProps) {
  const toast = useToast();
  const [confirmAction, setConfirmAction] = useState<RoleAction | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSelf = user.id != null && user.id === viewerId;
  const roles = user.roles ?? [];
  const isAdmin = roles.includes('Admin');
  const isFounder = roles.includes('Founder');
  const isVerifier = roles.includes('Verifier');
  const name = user.fullName || user.email || 'este usuario';

  const apiForAction: Record<RoleAction, () => Promise<unknown>> = {
    'promote': () => adminApi.promoteToAdmin(user.id!),
    'demote': () => adminApi.demoteFromAdmin(user.id!),
    'set-founder': () => adminApi.setFounder(user.id!),
    'remove-founder': () => adminApi.removeFounder(user.id!),
    'set-verifier': () => adminApi.setVerifier(user.id!),
    'remove-verifier': () => adminApi.removeVerifier(user.id!),
  };

  const SUCCESS_MSG: Record<RoleAction, string> = {
    'promote': 'Admin role granted.',
    'demote': 'Admin role removed.',
    'set-founder': 'Founder role granted.',
    'remove-founder': 'Founder role removed.',
    'set-verifier': 'Verifier role granted.',
    'remove-verifier': 'Verifier role removed.',
  };

  const handleConfirm = async () => {
    if (!confirmAction || !user.id) return;
    setSubmitting(true);
    try {
      await apiForAction[confirmAction]();
      toast(SUCCESS_MSG[confirmAction], 'success');
      onRefetch();
    } catch {
      toast('Action failed. Try again.', 'error');
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  return (
    <>
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--surface-base)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--warm-gray-50)',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Role management
          </p>
        </div>

        <div style={{ padding: '0 16px' }}>
          <RoleRow
            label="Admin"
            description="Panel access, credential review"
            hasRole={isAdmin}
            isSelf={isSelf}
            onGrant={() => setConfirmAction('promote')}
            onRevoke={() => setConfirmAction('demote')}
          />
          <RoleRow
            label="Founder"
            description="Manage administrator roles"
            hasRole={isFounder}
            isSelf={isSelf}
            onGrant={() => setConfirmAction('set-founder')}
            onRevoke={() => setConfirmAction('remove-founder')}
          />
          <RoleRow
            label="Verifier"
            description="Extended credential review"
            hasRole={isVerifier}
            isSelf={isSelf}
            isLast
            onGrant={() => setConfirmAction('set-verifier')}
            onRevoke={() => setConfirmAction('remove-verifier')}
          />
        </div>
      </div>

      <ConfirmModal
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        submitting={submitting}
        action={confirmAction ?? 'promote'}
        userName={name}
      />
    </>
  );
}

/* ─── UserDetailPage ─────────────────────────────────────────────────────── */

type FetchState =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'error'; message: string }
  | { status: 'ok'; user: AdminUserDto };

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const viewer = useCurrentUser();

  const [state, setState] = useState<FetchState>({ status: 'loading' });

  const fetchUser = useCallback(async () => {
    if (!id) { setState({ status: 'not-found' }); return; }
    setState({ status: 'loading' });
    try {
      const user = await adminApi.getUserById(id);
      setState({ status: 'ok', user });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setState({ status: 'not-found' });
      } else {
        setState({ status: 'error', message: 'Failed to load user.' });
      }
    }
  }, [id]);

  useEffect(() => { void fetchUser(); }, [fetchUser]);

  if (state.status === 'loading') return <DetailSkeleton />;

  if (state.status === 'not-found') {
    return (
      <div style={{ maxWidth: 760 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')} style={{ marginBottom: 20 }}>
          ← Back
        </Button>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--badge-rejected-text)', marginBottom: 12 }}>
          User not found.
        </p>
        <Button variant="secondary" size="sm" onClick={() => navigate('/users')}>
          Go to users
        </Button>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={{ maxWidth: 760 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')} style={{ marginBottom: 20 }}>
          ← Back
        </Button>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--badge-rejected-text)', marginBottom: 12 }}>
          {state.message}
        </p>
        <Button variant="secondary" size="sm" onClick={() => void fetchUser()}>
          Retry
        </Button>
      </div>
    );
  }

  const { user } = state;
  const v = user.credentialStatus ?? VerificationStatus.PendingDocuments;
  const hasCredential = v !== VerificationStatus.PendingDocuments;
  const isFounderViewer = viewer.roles.includes('Founder');
  const name = user.fullName || '—';
  const roles = user.roles ?? [];

  const identityItems: DataGridItem[] = [
    { label: 'Email', value: user.email },
    { label: 'Phone', value: user.phoneNumber },
    {
      label: 'Email confirmed',
      value: user.emailConfirmed ? 'Yes' : (
        <span style={{ color: 'var(--badge-rejected-text)' }}>No</span>
      ),
    },
    { label: 'Registered', value: formatDate(user.createdAt) },
    {
      label: 'Activity',
      value: `${user.postCount ?? 0} post${(user.postCount ?? 0) !== 1 ? 's' : ''}, ${user.commentCount ?? 0} comment${(user.commentCount ?? 0) !== 1 ? 's' : ''}`,
    },
  ];

  const credentialItems: DataGridItem[] = [
    {
      label: 'Credential status',
      value: (
        <Badge variant={CRED_STATUS_BADGE[v]}>{CRED_STATUS_LABEL[v]}</Badge>
      ),
    },
    { label: 'Submitted', value: formatDate(user.credentialSubmittedAt) },
    { label: 'Reviewed', value: formatDate(user.credentialReviewedAt) },
  ];

  return (
    <div style={{ maxWidth: 760 }}>
      {/* ── Back ─────────────────────────────────────────────────────────── */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
        ← Volver
      </Button>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-xl)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              margin: '0 0 6px',
              letterSpacing: '-0.02em',
            }}
          >
            {name}
          </h2>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {user.email ?? '—'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap', marginTop: 4 }}>
          {roles.length === 0 ? (
            <Badge variant="neutral">User</Badge>
          ) : (
            roles.map((r) => <RoleBadge key={r} role={r} />)
          )}
        </div>
      </div>

      {/* ── Identity ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <SectionHeader label="Identity" />
        <DataGrid items={identityItems} columns={2} />
      </div>

      {/* ── Credential ───────────────────────────────────────────────────── */}
      {hasCredential && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader label="Credential" />
          <DataGrid items={credentialItems} columns={3} />
          <div style={{ marginTop: 8 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/credentials?userEmail=${encodeURIComponent(user.email ?? '')}`)}
            >
              View credential history →
            </Button>
          </div>
        </div>
      )}

      {/* ── Role mutation (Founder-only UI gate) ─────────────────────────── */}
      {isFounderViewer && (
        <div style={{ marginTop: 8 }}>
          <RoleMutationSection
            user={user}
            viewerId={viewer.id}
            onRefetch={() => void fetchUser()}
          />
        </div>
      )}

      {/* ── Note for non-Founder admins ───────────────────────────────────── */}
      {!isFounderViewer && (
        <p style={{ marginTop: 16, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
          Role management requires the Founder role.
        </p>
      )}
    </div>
  );
}
